import {
    type ChangeEvent,
    type FormEvent,
    useEffect,
    useState
} from 'react';
import {useParams, useNavigate} from 'react-router-dom';
import {useIntl} from 'react-intl';
import {useDispatch, useSelector} from 'react-redux';
import type {Dispatch, ThunkDispatch, UnknownAction} from '@reduxjs/toolkit';
import SidePanel from '@components/shared/SidePanel/SidePanel';
import {AppButton} from '@components/Core/buttons/AppButton/AppButton';
import {AppOutlinedButton} from '@components/Core/buttons/AppOutlinedButton/AppOutlinedButton';
import {
    selectIsDeclinePanelOpen,
    selectRecipientId,
    setIsDeclinePanelOpen,
    createDeclineReason,
    selectIsCreateDeclineReasonLoading,
    selectTransactionId,
    exitFaceToFaceSigning
} from '@components/SignerUi/signerUiSlice';
import useNextPendingDocumentId from '@components/SignerUi/hooks/useNextPendingDocumentId';
import {useFaceToFaceRole} from '@components/SignerUi/hooks/useFaceToFaceRole';
import AppInput from '@components/Core/inputs/AppInput/AppInput';
import {
    PACKAGE_DECLINE_STARTED_EVENT,
    PACKAGE_DECLINE_SUCCESS_EVENT,
    PACKAGE_DECLINE_ERROR_EVENT
} from '@utils/iFrameNotifyer/events';
import IFrameEventNotifications from '@utils/iFrameNotifyer/IFrameEventNotifications';
import type {RootState} from '@components/SignerUi/signerUiSlice.types';
import {getCeremonyDeclinedPath} from '@constants/routes';
import {CEREMONY_F2F_PATH, TRANSACTION_PATH, CEREMONY_PATH} from '@constants/index';
import './declinePanel.less';

type AppDispatch = ThunkDispatch<RootState, undefined, UnknownAction> & Dispatch;

export default function DeclinePanel() {
    const intl = useIntl();
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const {documentId: documentIdFromUrl} = useParams();
    const nextPendingDocumentId = useNextPendingDocumentId();
    const recipientId = useSelector(selectRecipientId);
    const transactionId = useSelector(selectTransactionId);
    const isDeclinePanelOpen = useSelector(selectIsDeclinePanelOpen);
    const isCreateDeclineReasonLoading = useSelector(selectIsCreateDeclineReasonLoading);
    const {isFaceToFaceRecipient} = useFaceToFaceRole();

    const documentId = documentIdFromUrl || nextPendingDocumentId;
    const iFrameEventNotifications = (IFrameEventNotifications.getInstance() as unknown) as InstanceType<typeof IFrameEventNotifications>;
    const [reasonValue, setReasonValue] = useState<string>('');

    function close() {
        dispatch(setIsDeclinePanelOpen(false));
    }

    async function decline(e: FormEvent<HTMLFormElement>) {
        e.preventDefault();
        if (!reasonValue || !documentId || !recipientId || !transactionId) {
            return null;
        }

        await iFrameEventNotifications.postEvent(PACKAGE_DECLINE_STARTED_EVENT);

        try {
            await dispatch(createDeclineReason({
                documentId,
                recipientId,
                reason: reasonValue
            })).unwrap();

            await iFrameEventNotifications.postEvent(PACKAGE_DECLINE_SUCCESS_EVENT);
            close();

            if (isFaceToFaceRecipient) {
                await dispatch(exitFaceToFaceSigning());
                navigate(`${TRANSACTION_PATH}/${transactionId}${CEREMONY_PATH}/${CEREMONY_F2F_PATH}`);
            } else {
                navigate(getCeremonyDeclinedPath(transactionId));
            }
        } catch (error) {
            await iFrameEventNotifications.postEvent(PACKAGE_DECLINE_ERROR_EVENT);
            console.error('Failed to decline transaction:', error);
        }
    }

    function changeReasonValue({target}: ChangeEvent<HTMLInputElement>) {
        setReasonValue(target.value);
    }

    useEffect(() => {
        if (!isDeclinePanelOpen) {
            setReasonValue('');
        }
    }, [isDeclinePanelOpen]);

    const buttonsNode = (
        <>
            <AppOutlinedButton onClick={close}>
                {intl.formatMessage({id: 'oss.components.DeclineToSign.cancel', defaultMessage: ''})}
            </AppOutlinedButton>
            <AppButton type="submit" disabled={!reasonValue}>
                {intl.formatMessage({id: 'oss.components.DeclineToSign.decline', defaultMessage: ''})}
            </AppButton>
        </>
    );

    return (
        <SidePanel
            className="decline-signing-panel"
            open={isDeclinePanelOpen}
            onClose={close}
            onCloseAway={close}
            onSubmit={decline}
            isLoading={isCreateDeclineReasonLoading}
            buttonsNode={buttonsNode}
            title={intl.formatMessage({id: 'oss.components.DeclineToSign.title', defaultMessage: ''})}
            titleNode={undefined}
            subtitle={undefined}
            firstFocusEl={undefined}
            scrollNodeRef={undefined}
        >
            <div className="decline-signing-panel-container">
                <div className="content-title">
                    {intl.formatMessage({id: 'oss.components.DeclineToSign.contentTitle', defaultMessage: ''})}
                </div>
                <div className="content">
                    {intl.formatMessage({id: 'oss.components.DeclineToSign.content', defaultMessage: ''})}
                </div>
                <AppInput
                    label={intl.formatMessage({id: 'oss.components.DeclineToSign.otherReasonLabel', defaultMessage: ''})}
                    classes={{root: 'other-reason'}}
                    multiline
                    minRows={5}
                    maxRows={5}
                    onChange={changeReasonValue}
                    value={reasonValue}
                />
            </div>
        </SidePanel>
    );
}
